export interface GestureState {
  isDragging: boolean;
  isPinching: boolean;
  pointers: Map<number, { x: number; y: number }>;
}

export interface GestureCallbacks {
  onDrag?: (dx: number, dy: number) => void;
  onPinch?: (scale: number) => void;
  onDoubleTap?: () => void;
}

const DOUBLE_TAP_THRESHOLD = 300; // ms
const DOUBLE_TAP_DISTANCE = 30; // px

export class GestureHandler {
  private state: GestureState = {
    isDragging: false,
    isPinching: false,
    pointers: new Map(),
  };

  private callbacks: GestureCallbacks;
  private element: HTMLElement;

  private lastTapTime = 0;
  private lastTapPosition = { x: 0, y: 0 };
  private initialPinchDistance = 0;

  private boundHandlePointerDown: (e: PointerEvent) => void;
  private boundHandlePointerMove: (e: PointerEvent) => void;
  private boundHandlePointerUp: (e: PointerEvent) => void;

  constructor(element: HTMLElement, callbacks: GestureCallbacks) {
    this.element = element;
    this.callbacks = callbacks;

    this.boundHandlePointerDown = this.handlePointerDown.bind(this);
    this.boundHandlePointerMove = this.handlePointerMove.bind(this);
    this.boundHandlePointerUp = this.handlePointerUp.bind(this);

    this.element.addEventListener('pointerdown', this.boundHandlePointerDown);
    this.element.addEventListener('pointermove', this.boundHandlePointerMove);
    this.element.addEventListener('pointerup', this.boundHandlePointerUp);
    this.element.addEventListener('pointercancel', this.boundHandlePointerUp);
  }

  getState(): GestureState {
    return { ...this.state, pointers: new Map(this.state.pointers) };
  }

  destroy(): void {
    this.element.removeEventListener('pointerdown', this.boundHandlePointerDown);
    this.element.removeEventListener('pointermove', this.boundHandlePointerMove);
    this.element.removeEventListener('pointerup', this.boundHandlePointerUp);
    this.element.removeEventListener('pointercancel', this.boundHandlePointerUp);
  }

  private handlePointerDown(e: PointerEvent): void {
    this.state.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (this.state.pointers.size === 1) {
      this.state.isDragging = true;
    } else if (this.state.pointers.size === 2) {
      this.state.isPinching = true;
      this.state.isDragging = false;
      this.initialPinchDistance = this.getPinchDistance();
    }
  }

  private handlePointerMove(e: PointerEvent): void {
    const prev = this.state.pointers.get(e.pointerId);
    if (!prev) return;

    if (this.state.isPinching && this.state.pointers.size === 2) {
      // Update pointer position
      this.state.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      const currentDistance = this.getPinchDistance();
      if (this.initialPinchDistance > 0 && this.callbacks.onPinch) {
        const scale = currentDistance / this.initialPinchDistance;
        this.callbacks.onPinch(scale);
      }
    } else if (this.state.isDragging && this.state.pointers.size === 1) {
      const dx = e.clientX - prev.x;
      const dy = e.clientY - prev.y;

      this.state.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (this.callbacks.onDrag) {
        this.callbacks.onDrag(dx, dy);
      }
    }
  }

  private handlePointerUp(e: PointerEvent): void {
    const wasPointer = this.state.pointers.has(e.pointerId);
    this.state.pointers.delete(e.pointerId);

    // Check for double tap
    if (wasPointer && this.state.pointers.size === 0) {
      const now = Date.now();
      const timeDiff = now - this.lastTapTime;
      const distance = Math.sqrt(
        Math.pow(e.clientX - this.lastTapPosition.x, 2) +
          Math.pow(e.clientY - this.lastTapPosition.y, 2)
      );

      if (timeDiff < DOUBLE_TAP_THRESHOLD && distance < DOUBLE_TAP_DISTANCE) {
        if (this.callbacks.onDoubleTap) {
          this.callbacks.onDoubleTap();
        }
        this.lastTapTime = 0;
      } else {
        this.lastTapTime = now;
        this.lastTapPosition = { x: e.clientX, y: e.clientY };
      }
    }

    if (this.state.pointers.size < 2) {
      this.state.isPinching = false;
      this.initialPinchDistance = 0;
    }

    if (this.state.pointers.size === 0) {
      this.state.isDragging = false;
    } else if (this.state.pointers.size === 1) {
      this.state.isDragging = true;
    }
  }

  private getPinchDistance(): number {
    const pointers = Array.from(this.state.pointers.values());
    if (pointers.length < 2) return 0;

    const [p1, p2] = pointers;
    return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
  }
}
